import { useContext } from 'react';
import { type DonationFrequencyEnum } from '@rivallapp/volosports-components';
import { RegistrationContext } from '../../../../contexts';
import { useRegistrationData } from '../../../../hooks';

type PaymentSummary = {
  loading: boolean;
  pricingSelected: 'MEMBER' | 'NONMEMBER';
  nonmemberPriceInCents?: number | null;
  memberPriceInCents?: number | null;
  feesInCents?: number | null;
  creditAmountInCents?: number | null;
  donationInCents?: number | null;
  donationFrequency?: DonationFrequencyEnum;
  promoCodeDiscountInCents?: number | null;
  monthlyCostOfVoloPassInCents?: number | null;
  totalCostInCents?: number | null;
};

/**
 * Helper function to untangle all of the messy bits from Registration Context into values that are
 * actually easy to work with.
 */
const usePaymentSummary = (type: 'dropin' | 'non-dropin'): PaymentSummary => {
  // @ts-ignore
  const [registerState] = useContext(RegistrationContext);

  const {
    pricingBreakdown: nonDropInPricingBreakdown,
    pricingLoading: nonDropInPricingLoading,
    dropInPriceData,
    dropInPriceLoading,
    addVpMembership,
    addVpTrial,
    creditAppliedAmount,
    donationAmount,
    donationFrequency,
    isPrepaidTeam,
  } = registerState;

  // Get Volo Pass Pricing
  const { monthlyDollarPrice, monthlyFee, registerDailyLoading } = useRegistrationData();
  const monthlyCostOfVoloPassInCents = monthlyDollarPrice * 100;
  const voloPassFeesInCents = monthlyFee * 100;

  // Drop In Pricing ===============================================================================

  // Figure out which dropin pricing to select based on if they are a current member or if they have
  // Volo Pass in their cart.
  const dropInPricingOptions = dropInPriceData?.dropinPricingForRegistration ?? {};
  const { currentUserPaymentCriteria } = dropInPricingOptions;
  const dropinBreakdown =
    addVpMembership || addVpTrial ? 'memberBreakdown' : currentUserPaymentCriteria;
  const dropInPricingBreakdown = dropInPricingOptions?.[dropinBreakdown];
  const dropInMemberPrice = dropInPricingOptions?.memberBreakdown?.priceCents;

  const dropInPricingSelected =
    (dropInMemberPrice !== null && dropInMemberPrice !== undefined && addVpMembership) ||
    addVpTrial ||
    dropinBreakdown === 'memberBreakdown';

  const dropInTotalCost = addVpMembership
    ? (dropInPricingBreakdown?.totalCents ?? 0) + monthlyCostOfVoloPassInCents + voloPassFeesInCents
    : dropInPricingBreakdown?.totalCents;

  // Non Dropin Pricing ============================================================================

  const nonDropInFees =
    nonDropInPricingBreakdown?.totalFeeCents !== null && (addVpMembership || addVpTrial)
      ? nonDropInPricingBreakdown.totalFeeCents + voloPassFeesInCents
      : nonDropInPricingBreakdown?.totalFeeCents;

  const { membershipDiscountApplied } = nonDropInPricingBreakdown;

  // Prepaid teams have a member price, which is a backend bug. We need to compensate for that here.
  const nonDropInHasMemberPrice =
    nonDropInPricingBreakdown?.memberPrice !== null &&
    nonDropInPricingBreakdown?.memberPrice !== undefined &&
    nonDropInPricingBreakdown?.memberPrice !== nonDropInPricingBreakdown?.originalPrice &&
    !isPrepaidTeam;
  const nonDropInMemberPrice = nonDropInHasMemberPrice
    ? nonDropInPricingBreakdown?.memberPrice
    : undefined;

  const nonDropInPricingSelected =
    nonDropInHasMemberPrice && (addVpMembership || addVpTrial || !!membershipDiscountApplied)
      ? 'MEMBER'
      : 'NONMEMBER';

  const nonDropInTotalCost = addVpMembership
    ? (nonDropInPricingBreakdown?.totalCents ?? 0) + monthlyCostOfVoloPassInCents + monthlyFee * 100
    : nonDropInPricingBreakdown?.totalCents;

  switch (type) {
    case 'dropin':
      return {
        loading: (dropInPriceLoading || registerDailyLoading) as boolean,
        pricingSelected: dropInPricingSelected,
        nonmemberPriceInCents: dropInPricingOptions?.nonmemberBreakdown?.priceCents,
        memberPriceInCents: dropInMemberPrice,
        feesInCents:
          addVpMembership || addVpTrial
            ? voloPassFeesInCents + (dropInPricingBreakdown?.totalFeeCents ?? 0)
            : dropInPricingBreakdown?.totalFeeCents,
        creditAmountInCents: Math.min(
          creditAppliedAmount,
          dropInPricingBreakdown?.programPriceCents
        ),
        donationInCents: donationAmount,
        donationFrequency,
        promoCodeDiscountInCents: dropInPricingBreakdown?.promoDiscountCents,
        monthlyCostOfVoloPassInCents,
        totalCostInCents: dropInTotalCost,
      };
    case 'non-dropin':
    default:
      return {
        loading: nonDropInPricingLoading as boolean,
        pricingSelected: nonDropInPricingSelected,
        nonmemberPriceInCents: nonDropInPricingBreakdown?.originalPrice as
          | number
          | undefined
          | null,
        memberPriceInCents: nonDropInHasMemberPrice ? nonDropInMemberPrice : undefined,
        feesInCents: nonDropInFees,
        creditAmountInCents: nonDropInPricingBreakdown?.creditAmount as number | undefined | null,
        donationInCents: donationAmount,
        donationFrequency,
        promoCodeDiscountInCents: nonDropInPricingBreakdown?.promoDiscountCents as
          | number
          | undefined
          | null,
        monthlyCostOfVoloPassInCents,
        totalCostInCents: nonDropInTotalCost,
      };
  }
};

export default usePaymentSummary;
